import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Switch } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { resolveDynamicBinding } from './bindings';
import { useContentKitConfirm } from './ContentKitConfirm';
import type { ContentKitElementProps, ContentKitElementRef } from './types';

/**
 * Renderer for a switch element.
 */
export const ContentKitSwitch = React.forwardRef(function ContentKitSwitchWithRef(
    props: ContentKitElementProps<api.ContentKitSwitch>,
    ref: React.Ref<ContentKitElementRef>
): React.ReactElement {
    const { element, localState, dispatchAction, setLocalState } = props;

    const value = Boolean(localState[element.state] || element.initialValue);

    const [dispatching, wrapDispatching] = useLoadingState(false);
    const [confirm, wrapConfirm] = useContentKitConfirm();

    /**
     * Dispatch action (if exists) when local state changes.
     */
    const onValueChange = (updatedState: object) => {
        const onChangeAction = element.onValueChange;
        if (onChangeAction) {
            const action = resolveDynamicBinding(updatedState, onChangeAction);
            wrapDispatching(() => dispatchAction(action));
        }
    };

    const press = () => {
        wrapConfirm(value ? undefined : element.confirm, () => {
            setLocalState((prev) => {
                const updated = {
                    ...prev,
                    [element.state]: !value,
                };
                onValueChange(updated);
                return updated;
            });
        });
    };

    React.useImperativeHandle(ref, () => ({
        press,
    }));

    return (
        <>
            <Switch
                value={value}
                disabled={element.disabled || dispatching}
                loading={dispatching}
                onValueChange={press}
            />
            {confirm}
        </>
    );
});
